/* ======================================================================
   CART FAST ADD — perceived + real add-to-cart speed.
   Reported: seconds between tapping ADD TO CART and the drawer opening,
   because the theme waits for TWO sequential network round trips
   (/cart/add.js with bundled section renders, then a full cart.js) before
   opening the drawer.

   1. INSTANT OPEN — the drawer opens with its built-in spinner the moment
      an add-to-cart form is submitted; content streams in when ready.
   2. FAST ADD — takes over the submit and dispatches cart:change straight
      from the add.js response (its section HTML is already final); the cart
      object is synthesized from the cached snapshot + added lines, with a
      background cart.js true-up. Falls back to the theme's two-trip order
      when no snapshot exists yet.
   3. TIMING — pushes an `atc_timing` event (duration_ms) to the dataLayer
      per add, so slow devices/connections are measurable.
   ====================================================================== */
(function () {
  'use strict';
  if (window.__skFastAddInit) return;
  window.__skFastAddInit = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cachedCart = null;
  var fastBusy = false;
  var atcT0 = 0;

  function track(name, data) {
    try {
      var payload = data || {};
      payload.event = name;
      (window.dataLayer = window.dataLayer || []).push(payload);
      document.dispatchEvent(new CustomEvent('sk:track', { detail: payload, bubbles: true }));
    } catch (e) {}
  }

  document.addEventListener('cart:change', function (e) {
    var c = e.detail && e.detail.cart;
    if (c && c.items) cachedCart = c;
    if (atcT0) {
      var ms = Math.round(((window.performance && performance.now()) || 0) - atcT0);
      atcT0 = 0;
      if (ms > 0) track('atc_timing', { duration_ms: ms });
    }
  });

  // Baseline snapshot, off the critical path.
  setTimeout(function () {
    if (cachedCart) return;
    fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }).then(function (c) {
      if (!cachedCart && c && c.items) cachedCart = c;
    }).catch(function () {});
  }, 2500);

  function synthesizeCart(cached, added) {
    var kept = (cached.items || []).filter(function (it) {
      return !added.some(function (a) { return a.key === it.key; });
    });
    var items = added.concat(kept);
    var count = 0;
    items.forEach(function (it) { count += it.quantity || 0; });
    var cart = {};
    for (var k in cached) { if (Object.prototype.hasOwnProperty.call(cached, k)) cart[k] = cached[k]; }
    cart.items = items;
    cart.item_count = count;
    return cart;
  }

  /* ---- FAST CHECKOUT ---------------------------------------------------
     The CHECKOUT button submits the entire cart form (POST /cart) and rides
     a redirect into the checkout. When the form carries nothing that must be
     posted (no note, no terms checkbox, no pending discount code, no app
     attribute inputs), that POST is a wasted round trip — navigate straight
     to /checkout instead. Any of those present → normal submit untouched. */
  document.addEventListener('click', function (event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    var t = path[0] && path[0].closest ? path[0].closest('button[name="checkout"], [type="submit"][name="checkout"]') : null;
    if (!t) return;
    var form = t.closest('form');
    if (form) {
      if (form.querySelector('textarea[name="note"], input[name="checkout_agreement"], [name^="attributes"]')) return;
      var dc = form.querySelector('input[name="discount"]');
      if (dc && dc.value) return;
    }
    event.preventDefault();
    try { t.setAttribute('aria-busy', 'true'); } catch (e) {}
    window.location.assign(ROOT.replace(/\/+$/, '') + '/checkout');
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var action = form && form.getAttribute && (form.getAttribute('action') || '');
    if (!action || action.indexOf('/cart/add') === -1) return;
    var settings = window.themeVariables && window.themeVariables.settings;
    if (settings && settings.cartType && settings.cartType !== 'drawer') return;
    if (settings && settings.pageType === 'cart') return;
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    if (typeof FormData === 'undefined' || !window.fetch) return;

    // 1 · Instant open + timing start.
    atcT0 = (window.performance && performance.now()) || 0;
    if (typeof drawer.show === 'function' && !drawer.hasAttribute('open')) {
      try { drawer.show(); } catch (e) {}
    }

    // 2 · Take the submit over (the theme's handler must not double-submit).
    event.preventDefault();
    event.stopPropagation();
    if (fastBusy) return;
    fastBusy = true;

    var submitButtons = Array.prototype.filter.call(form.elements, function (el) { return el.type === 'submit'; });
    submitButtons.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    var bundle = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: bundle } }));
    var formData = new FormData(form);
    formData.set('sections', Array.from(bundle).join(','));

    function finish() {
      fastBusy = false;
      submitButtons.forEach(function (b) { b.removeAttribute('aria-busy'); });
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
    function dispatchChange(cart, items) {
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: items, cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'variant:add', cart: cart }
      }));
    }

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        return response.json().then(function (json) { return { ok: response.ok, json: json }; });
      })
      .then(function (res) {
        if (!res.ok) {
          finish();
          form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: res.json && res.json.message } }));
          document.dispatchEvent(new CustomEvent('toast:show', {
            detail: { message: (res.json && (res.json.description || res.json.message)) || 'Could not add to cart', tone: 'error' }
          }));
          return;
        }
        var items = res.json && res.json.items ? res.json.items : [res.json];
        if (cachedCart && cachedCart.items) {
          finish();
          var cart = synthesizeCart(cachedCart, items);
          cart.sections = res.json.sections;
          dispatchChange(cart, items);
          fetch(ROOT + 'cart.js').then(function (r) { return r.json(); })
            .then(function (c) { if (c && c.items) cachedCart = c; }).catch(function () {});
        } else {
          fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }).then(function (c) {
            finish();
            c.sections = res.json.sections;
            dispatchChange(c, items);
          }).catch(function () {
            finish();
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          });
        }
      })
      .catch(function () {
        finish();
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
  }, true);
})();
